import { AutoAdjustBackwardsLimit } from "../../constants/autoAdjustBackwardsLimit";
import { AutoAdjustDateLevel } from "../../constants/autoAdjustDateLevel";
import compactDisplays from "../../constants/compactDisplay";
import defaultGenericNarrativeLabels from "../../constants/defaultGenericNarrativeLabels";
import itemTypeCategories from "../../constants/itemTypeCategories";
import itemTypeIds from "../../constants/itemTypeIds";
import narrativeNumberingOptions from "../../constants/narrativeNumberingOptions";
import { REFERENCE_RELATES_TO } from "../../constants/references";
import subwayDisplayOptions from "../../constants/subwayDisplayOptions";
import { TimelineZoomLimits } from "../../constants/zoomRange";
import { getGregorianCalendarObject } from "../../modules/definition/calendar/gregorianCalendarRaw";

const calendar = getGregorianCalendarObject();

export default {
  autoAdjustLevel: AutoAdjustDateLevel.ResolveConstraints,
  autoAdjustBackwardsLimit: AutoAdjustBackwardsLimit.Off,
  includeNarrative: false,
  types: {
    byId: {
      [itemTypeIds.NARRATIVE_FOLDER_TYPE_ID]: {
        enabled: false,
        canDelete: false,
        id: itemTypeIds.NARRATIVE_FOLDER_TYPE_ID,
        label: "Narrative Folder",
        pluralLabel: "Narrative Folders",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        references: [REFERENCE_RELATES_TO],
        properties: [],
        superTypes: [],
        icon: "folder",
        color: "iconMono",
        canHaveDate: false,
        canIncludeInNarrative: true,
        showInSequence: false,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "NF",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isRelative: false,
        relativeUnits: "seconds",
        defaultSequenceType: false,
        defaultNarrativeType: false,
        isNarrativeFolder: true
      },
      [itemTypeIds.MINDMAP_NODE_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.MINDMAP_NODE_TYPE_ID,
        label: "Mindmap Node",
        pluralLabel: "Nodes",
        references: [REFERENCE_RELATES_TO],
        properties: [],
        superTypes: [],
        icon: "diamond",
        color: "iconMono",
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "NO",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isRelative: false,
        relativeUnits: "seconds"
      },
      [itemTypeIds.EVENT_ITEM_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.EVENT_ITEM_TYPE_ID,
        label: "Event",
        pluralLabel: "Events",
        references: ["personRef", REFERENCE_RELATES_TO],
        properties: [],
        superTypes: [itemTypeIds.EVENT_ITEM_TYPE_ID],
        icon: "diamond",
        color: "iconYellow",
        canHaveDate: true,
        canIncludeInNarrative: true,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "EV",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isRelative: true,
        relativeUnits: "seconds",
        defaultSequenceType: true
      },
      [itemTypeIds.PERSON_ITEM_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.PERSON_ITEM_TYPE_ID,
        label: "Person",
        pluralLabel: "People",
        references: [REFERENCE_RELATES_TO],
        properties: [],
        superTypes: [],
        icon: "person",
        color: "iconRed",
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        columnIsVisible: true,
        identifierPrefix: "PER",
        compactDisplay: compactDisplays.AVATAR_INITIALS,
        category: itemTypeCategories.PERSON,
        isRelative: false,
        relativeUnits: "seconds"
      }
    },
    allIds: [
      itemTypeIds.EVENT_ITEM_TYPE_ID,
      itemTypeIds.PERSON_ITEM_TYPE_ID,
      itemTypeIds.NARRATIVE_FOLDER_TYPE_ID,
      itemTypeIds.MINDMAP_NODE_TYPE_ID
    ]
  },
  properties: {
    byId: {},
    allIds: []
  },
  references: {
    byId: {
      personRef: {
        enabled: true,
        canDelete: true,
        id: "personRef",
        key: null,
        label: "Person",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "lightTeal",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      [REFERENCE_RELATES_TO]: {
        enabled: true,
        canDelete: false,
        id: REFERENCE_RELATES_TO,
        key: null,
        label: "Relates to",
        types: [
          itemTypeIds.MINDMAP_NODE_TYPE_ID,
          itemTypeIds.EVENT_ITEM_TYPE_ID,
          itemTypeIds.PERSON_ITEM_TYPE_ID,
          itemTypeIds.NARRATIVE_FOLDER_TYPE_ID
        ],
        icon: "circle",
        color: "mono",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      }
    },
    allIds: ["personRef", REFERENCE_RELATES_TO]
  },
  calendar,
  colors: {
    byId: {
      "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC": {
        id: "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC",
        label: "Light Purple",
        swatchColor: "lightPurple"
      },
      "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5": {
        id: "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5",
        label: "Light Teal",
        swatchColor: "lightTeal"
      },
      "88F01362-D7BA-4AD9-8C93-E0EC45A604B4": {
        id: "88F01362-D7BA-4AD9-8C93-E0EC45A604B4",
        label: "Denim",
        swatchColor: "denim"
      },
      "D6DB1C49-D913-4D7C-884B-1F7AF1F53187": {
        id: "D6DB1C49-D913-4D7C-884B-1F7AF1F53187",
        label: "Yellow",
        swatchColor: "yellow"
      },
      "199ABF77-578A-4EA8-8C2D-8EA080610A78": {
        id: "199ABF77-578A-4EA8-8C2D-8EA080610A78",
        label: "Dark Purple",
        swatchColor: "darkPurple"
      },
      "D9DAECFF-F3CE-4399-9690-5E045E8BFA49": {
        id: "D9DAECFF-F3CE-4399-9690-5E045E8BFA49",
        label: "Light Blue",
        swatchColor: "lightBlue"
      },
      "0CAFEDAF-A92E-4012-9E43-438B826F5FC7": {
        id: "0CAFEDAF-A92E-4012-9E43-438B826F5FC7",
        label: "Apple",
        swatchColor: "apple"
      },
      "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7": {
        id: "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7",
        label: "Red",
        swatchColor: "red"
      },
      "7F4245A2-AF3B-4633-BEBA-68494486C574": {
        id: "7F4245A2-AF3B-4633-BEBA-68494486C574",
        label: "Peach",
        swatchColor: "peach"
      }
    },
    allIds: [
      "0CAFEDAF-A92E-4012-9E43-438B826F5FC7",
      "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC",
      "199ABF77-578A-4EA8-8C2D-8EA080610A78",
      "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5",
      "7F4245A2-AF3B-4633-BEBA-68494486C574",
      "88F01362-D7BA-4AD9-8C93-E0EC45A604B4",
      "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7",
      "D6DB1C49-D913-4D7C-884B-1F7AF1F53187",
      "D9DAECFF-F3CE-4399-9690-5E045E8BFA49"
    ]
  },
  timelineViewSettings: {
    scrollBounds: "infinite",
    eventLayout: "clearAll",
    showIdentifier: false,
    showDates: true,
    showDuration: false,
    showNarrative: false,
    showRelationshipTypes: [],
    showImage: false,
    showSummary: false,
    showInfoIcons: false,
    showDependencies: true,
    zoomFrom: TimelineZoomLimits.Century,
    zoomTo: TimelineZoomLimits.Day,
    showTypes: [itemTypeIds.EVENT_ITEM_TYPE_ID]
  },
  narrativeViewSettings: {
    showIdentifier: false,
    showDates: false,
    showDuration: false,
    showNarrative: true,
    showRelatedAvatars: true,
    showImage: false,
    showSummary: false,
    showInfoIcons: false,
    columnLevel: 0
  },
  narrativeSettings: {
    labels: defaultGenericNarrativeLabels,
    autoDeleteWhenRemoveContainer: true,
    numbering: narrativeNumberingOptions.CONTINUOUS_STYLE
  },
  mindmapViewSettings: {
    showIdentifier: true,
    showDates: false,
    showDuration: false,
    showNarrative: false,
    showRelationshipTypes: [],
    showImage: false,
    showSummary: true,
    showInfoIcons: false
  },
  subwayViewSettings: {
    showDates: false,
    display: subwayDisplayOptions.SUBWAY
  },
  relationshipViewSettings: {
    showIdentifier: false,
    showDates: true,
    showDuration: false,
    showNarrative: false,
    showSummary: false,
    showInfoIcons: false
  }
};
